import { create } from 'zustand';
import { servicesContainer } from '@/core/di/services-container';
import { useOperatorStore } from '@/core/stores/operator-store';
import {
  AssignKioskState,
  AssignKioskActions,
  KioskLocation,
  KioskConnector,
} from '../types/assign-kiosk-types';
import { buildSettingsJson } from '../utils/build-settings-json';

type Store = AssignKioskState & AssignKioskActions;

const initialState: AssignKioskState = {
  machineId: null,
  screenType: null,
  kioskDetails: null,
  locations: [],
  selectedLocationId: null,
  expandedLocationId: null,
  locationSearchQuery: '',
  isLoadingInit: false,
  isLoadingLocations: false,
  locationsFetched: false,
  isAssigning: false,
  initError: null,
  locationsError: null,
  assignError: null,
  assignSuccess: false,
  assignSuccessMessage: 'Kiosk assigned successfully.',
  locationsSnapshot: [],
  selectedLocationIdSnapshot: null,
  connectorLimitError: null,
  defaultCredit: null,
  isLoadingDefaultCredit: false,
};

/** Immutably update connector state inside the locations array. */
function patchConnector(
  locations: KioskLocation[],
  locationId: string,
  evseUid: string,
  connectorId: string,
  patch: Partial<KioskConnector>
): KioskLocation[] {
  return locations.map((loc) => {
    if (loc.id !== locationId) return loc;
    return {
      ...loc,
      evses: loc.evses.map((evse) => {
        if (evse.uid !== evseUid) return evse;
        return {
          ...evse,
          connectors: evse.connectors.map((c): KioskConnector =>
            c.id !== connectorId ? c : { ...c, ...patch }
          ),
        };
      }),
    };
  });
}

export const useAssignKioskStore = create<Store>((set, get) => ({
  ...initialState,

  // ─── initialize ────────────────────────────────────────────────────────────
  initialize: async (machineId: string) => {
    set({ ...initialState, machineId, isLoadingInit: true });

    const { getMachineModelUseCase, getKioskDetailsUseCase } =
      servicesContainer.getServices();

    const modelResult = await getMachineModelUseCase.execute(machineId);

    // Machine model is required — fatal if missing
    if (modelResult.hasError) {
      set({
        isLoadingInit: false,
        initError: modelResult.errorMessage ?? 'Failed to initialise screen',
      });
      return;
    }

    // Fetch kiosk details for EV_KIOSK and UNKNOWN
    const kioskDetails =
      modelResult.screenType === 'EV_KIOSK' || modelResult.screenType === 'UNKNOWN'
        ? (await getKioskDetailsUseCase.execute(machineId)).data ?? null
        : null;

    set({
      isLoadingInit: false,
      screenType: modelResult.screenType,
      kioskDetails,
    });

    // FALLBACK (UNKNOWN machine model) — no locations to fetch
    if (modelResult.screenType === 'FALLBACK') return;

    // Auto-fetch locations immediately (matching Flutter's init flow)
    await get().fetchLocations();
  },

  // ─── loadDefaultCredit ─────────────────────────────────────────────────────
  // Non-fatal: on failure defaultCredit stays null (no ceiling, generic tooltip).
  loadDefaultCredit: async (machineId: string) => {
    set({ defaultCredit: null, isLoadingDefaultCredit: true });
    const { getMachineDefaultCreditUseCase } = servicesContainer.getServices();
    const result = await getMachineDefaultCreditUseCase.execute(machineId);
    // Ignore a stale response if the machine changed while loading
    if (get().machineId !== machineId) return;
    set({ defaultCredit: result.data, isLoadingDefaultCredit: false });
  },

  // ─── fetchLocations ────────────────────────────────────────────────────────
  fetchLocations: async () => {
    const { kioskDetails, machineId, screenType } = get();

    set({ isLoadingLocations: true, locationsError: null, locations: [] });

    // ── SPARK_DEVICE_START / SPARK_REMOTE_START: dedicated endpoints ──────
    if (screenType === 'SPARK_DEVICE_START' || screenType === 'SPARK_REMOTE_START') {
      if (!machineId) {
        set({ isLoadingLocations: false, locationsFetched: true, locationsError: 'Machine ID not available.' });
        return;
      }

      const services = servicesContainer.getServices();
      const useCase = screenType === 'SPARK_REMOTE_START'
        ? services.getRemoteStartLocationsUseCase
        : services.getSparkLocationsUseCase;
      const result = await useCase.execute(machineId);

      if (result.hasError) {
        set({ isLoadingLocations: false, locationsFetched: true, locationsError: result.errorMessage });
        return;
      }

      const assignedLocation = result.assignedLocationId
        ? result.locations.find((l) => l.id === result.assignedLocationId)
        : null;

      set({
        isLoadingLocations: false,
        locationsFetched: true,
        locations: result.locations,
        selectedLocationId: assignedLocation?.id ?? null,
        expandedLocationId: assignedLocation?.id ?? null,
        locationsSnapshot: result.locations,
        selectedLocationIdSnapshot: assignedLocation?.id ?? null,
      });
      return;
    }

    // ── EV_KIOSK / OCPI / FALLBACK: existing kiosk-locations endpoint ──────
    // Use operatorId from the kiosk API response.
    // If the kiosk is unassigned (no operator in response), fall back to machineId filter.
    // Do NOT use the browser-stored operator — it may belong to a different operator context.
    const operatorId = kioskDetails?.operator?.toString() ?? null;

    if (!operatorId && !machineId) {
      set({ isLoadingLocations: false, locationsError: 'Operator ID not available. Please select an operator first.' });
      return;
    }

    const { getKioskLocationsUseCase } = servicesContainer.getServices();
    const assignedLocationId = kioskDetails?.locationId ?? undefined;
    const result = await getKioskLocationsUseCase.execute(
      operatorId,
      machineId,
      kioskDetails?.settings ?? null,
      assignedLocationId
    );

    if (result.hasError) {
      set({ isLoadingLocations: false, locationsFetched: true, locationsError: result.errorMessage });
      return;
    }

    // Auto-select the currently assigned location (if it's in the list)
    const assignedId = kioskDetails?.locationId ?? null;
    const assignedLocation = assignedId
      ? result.locations.find((l) => l.id === assignedId)
      : null;

    let locations = result.locations;
    if (assignedLocation) {
      let savedFilter: unknown[] = [];
      try {
        const parsed = kioskDetails?.settings ? JSON.parse(kioskDetails.settings) : null;
        if (Array.isArray(parsed?.filter)) savedFilter = parsed.filter;
      } catch { /* ignore parse errors */ }

      const hasAnySelected = assignedLocation.evses
        .flatMap((e) => e.connectors)
        .some((c) => c.isSelected);
      if (!hasAnySelected && savedFilter.length === 0) {
        locations = locations.map((loc) => {
          if (loc.id !== assignedLocation.id) return loc;
          return {
            ...loc,
            evses: loc.evses.map((evse) => ({
              ...evse,
              connectors: evse.connectors.map((c): KioskConnector => ({ ...c, isSelected: true })),
            })),
          };
        });
      }
    }

    set({
      isLoadingLocations: false,
      locationsFetched: true,
      locations,
      selectedLocationId: assignedLocation?.id ?? null,
      expandedLocationId: assignedLocation?.id ?? null,
      locationsSnapshot: locations,
      selectedLocationIdSnapshot: assignedLocation?.id ?? null,
    });
  },

  // ─── fetchAllLocations ────────────────────────────────────────────────────
  // Called when the user clicks "Change Location" — always loads the full list.
  fetchAllLocations: async () => {
    const { kioskDetails, machineId, screenType } = get();

    // ── SPARK_DEVICE_START / SPARK_REMOTE_START ───────────────────────────
    // SPARK types already have all locations from the initial fetch — nothing to do
    if (screenType === 'SPARK_DEVICE_START' || screenType === 'SPARK_REMOTE_START') return;

    if (screenType !== 'EV_KIOSK' && screenType !== 'UNKNOWN') return;

    set({ isLoadingLocations: true, locationsError: null });

    // Use operatorId from the kiosk API response.
    // If the kiosk is unassigned (no operator in response), fall back to machineId filter.
    // Do NOT use the browser-stored operator — it may belong to a different operator context.
    const operatorId = kioskDetails?.operator?.toString() ?? null;

    if (!operatorId && !machineId) {
      set({ isLoadingLocations: false, locationsError: 'Operator ID not available. Please select an operator first.' });
      return;
    }

    const { getKioskLocationsUseCase } = servicesContainer.getServices();
    // No locationId filter — fetch all locations for operator
    const result = await getKioskLocationsUseCase.execute(operatorId, machineId, kioskDetails?.settings ?? null);

    if (result.hasError) {
      set({ isLoadingLocations: false, locationsError: result.errorMessage });
      return;
    }

    // Preserve connector state for the already-assigned location
    const { locations: currentLocations } = get();
    const merged = result.locations.map((loc) => {
      const existing = currentLocations.find((l) => l.id === loc.id);
      return existing ?? loc;
    });

    set({ isLoadingLocations: false, locations: merged });
  },

  // ─── setLocationSearch ─────────────────────────────────────────────────────
  setLocationSearch: (query: string) => set({ locationSearchQuery: query }),

  // ─── toggleLocationSwitch ─────────────────────────────────────────────────
  toggleLocationSwitch: (locationId: string) => {
    const { selectedLocationId, locations, screenType } = get();
    const isSparkType = screenType === 'SPARK_DEVICE_START' || screenType === 'SPARK_REMOTE_START';

    if (selectedLocationId === locationId) {
      // Toggle OFF: deselect and collapse
      set({ selectedLocationId: null, expandedLocationId: null, connectorLimitError: null });
    } else {
      // Deselect connectors in the previously selected location (all types).
      // For SPARK: do NOT auto-select connectors in the new location — user must pick explicitly.
      // For non-SPARK: auto-check all connectors in the new location.
      const updated = locations.map((loc) => {
        if (loc.id === locationId && !isSparkType) {
          return {
            ...loc,
            evses: loc.evses.map((evse) => ({
              ...evse,
              connectors: evse.connectors.map((c): KioskConnector => ({ ...c, isSelected: true })),
            })),
          };
        }
        if (loc.id === selectedLocationId) {
          return {
            ...loc,
            evses: loc.evses.map((evse) => ({
              ...evse,
              connectors: evse.connectors.map((c): KioskConnector => ({ ...c, isSelected: false })),
            })),
          };
        }
        return loc;
      });
      set({ selectedLocationId: locationId, expandedLocationId: locationId, locations: updated, connectorLimitError: null });
    }
  },

  // ─── setExpandedLocation ──────────────────────────────────────────────────
  setExpandedLocation: (locationId: string | null) => {
    set({ expandedLocationId: locationId });
  },

  // ─── toggleConnector ──────────────────────────────────────────────────────
  toggleConnector: (locationId: string, evseUid: string, connectorId: string) => {
    const { locations, selectedLocationId, screenType } = get();
    const isSparkType = screenType === 'SPARK_DEVICE_START' || screenType === 'SPARK_REMOTE_START';
    const loc = locations.find((l) => l.id === locationId);
    if (!loc) return;
    const evse = loc.evses.find((e) => e.uid === evseUid);
    const connector = evse?.connectors.find((c) => c.id === connectorId);
    const newValue = !connector?.isSelected;

    // SPARK: enforce max 6 connectors
    if (isSparkType && newValue) {
      const selectedCount = loc.evses.flatMap((e) => e.connectors).filter((c) => c.isSelected).length;
      if (selectedCount >= 6) {
        set({ connectorLimitError: 'Only up to 6 connectors can be assigned at a time.' });
        setTimeout(() => set({ connectorLimitError: null }), 10000);
        return;
      }
    }
    if (isSparkType) set({ connectorLimitError: null });
    // If checking a connector in a different location, clear the old location's connectors first
    let baseLocations = locations;
    if (newValue && selectedLocationId && selectedLocationId !== locationId) {
      baseLocations = locations.map((loc) => {
        if (loc.id !== selectedLocationId) return loc;
        return {
          ...loc,
          evses: loc.evses.map((evse) => ({
            ...evse,
            connectors: evse.connectors.map((c): KioskConnector => ({ ...c, isSelected: false })),
          })),
        };
      });
    }
    const updatedLocations = patchConnector(baseLocations, locationId, evseUid, connectorId, {
      isSelected: newValue,
    });
    // If all connectors in this location are now deselected, unselect the location
    const updatedLoc = updatedLocations.find((l) => l.id === locationId);
    const anySelected = updatedLoc?.evses.flatMap((e) => e.connectors).some((c) => c.isSelected) ?? false;

    if (isSparkType && selectedLocationId === locationId && !anySelected) {
      // SPARK: keep the location selected so the "not registered" banner doesn't flash, show brief warning
      set({ locations: updatedLocations, connectorLimitError: 'You must select at least one connector.' });
      setTimeout(() => set({ connectorLimitError: null }), 10000);
    } else {
      set({
        locations: updatedLocations,
        ...(anySelected && selectedLocationId !== locationId
          ? { selectedLocationId: locationId }
          : {}),
      });
    }
  },

  // ─── clearAllConnectors ───────────────────────────────────────────────────
  clearAllConnectors: (locationId: string) => {
    const { locations, screenType } = get();
    const isSparkType = screenType === 'SPARK_DEVICE_START' || screenType === 'SPARK_REMOTE_START';
    set({
      locations: locations.map((loc) => {
        if (loc.id !== locationId) return loc;
        return {
          ...loc,
          evses: loc.evses.map((evse) => ({
            ...evse,
            connectors: evse.connectors.map((c): KioskConnector => ({
              ...c,
              isSelected: false,
              displayOrder: null,
              alias: '',
            })),
          })),
        };
      }),
      ...(isSparkType ? { connectorLimitError: 'You must select at least one connector.' } : {}),
    });
    if (isSparkType) {
      setTimeout(() => set({ connectorLimitError: null }), 10000);
    }
  },

  // ─── setSelectedLocationId ────────────────────────────────────────────────
  setSelectedLocationId: (id: string | null) => {
    set({ selectedLocationId: id });
  },

  // ─── updateConnectorAlias ─────────────────────────────────────────────────
  updateConnectorAlias: (
    locationId: string,
    evseUid: string,
    connectorId: string,
    alias: string
  ) => {
    set({
      locations: patchConnector(get().locations, locationId, evseUid, connectorId, { alias }),
    });
  },

  // ─── updateConnectorOrder ─────────────────────────────────────────────────
  updateConnectorOrder: (
    locationId: string,
    evseUid: string,
    connectorId: string,
    order: number | null
  ) => {
    set({
      locations: patchConnector(get().locations, locationId, evseUid, connectorId, {
        displayOrder: order,
      }),
    });
  },

  // ─── assignLocation ───────────────────────────────────────────────────────
  assignLocation: async () => {
    const { machineId, selectedLocationId, locations, kioskDetails, screenType } = get();
    if (!machineId || !selectedLocationId) return;

    const selectedLocation = locations.find((l) => l.id === selectedLocationId);
    if (!selectedLocation) return;

    set({ isAssigning: true, assignError: null, assignSuccess: false });

    // ── SPARK_DEVICE_START / SPARK_REMOTE_START: PUT mapping endpoint ─────
    if (screenType === 'SPARK_DEVICE_START' || screenType === 'SPARK_REMOTE_START') {
      const services = servicesContainer.getServices();
      const useCase = screenType === 'SPARK_REMOTE_START'
        ? services.saveRemoteStartMappingUseCase
        : services.saveSparkMappingUseCase;
      const result = await useCase.execute(machineId, selectedLocation);

      if (result.hasError) {
        set({ isAssigning: false, assignError: result.errorMessage });
        setTimeout(() => {
          const current = useAssignKioskStore.getState();
          if (current.assignError === result.errorMessage) {
            useAssignKioskStore.setState({ assignError: null });
          }
        }, 10_000);
        return;
      }

      set({ isAssigning: false, assignSuccess: true });
      setTimeout(() => { useAssignKioskStore.setState({ assignSuccess: false }); }, 4_000);
      await get().fetchLocations();
      return;
    }

    // ── EV_KIOSK / OCPI / FALLBACK: registerKiosk endpoint ────────────────
    const selectedConnectorCount = selectedLocation.evses.flatMap((e) => e.connectors).filter((c) => c.isSelected).length;
    const isConnectorRemoval = selectedConnectorCount === 0;
    const { countryCode, partyId, id: locationId } = selectedLocation;
    if (!countryCode || !partyId || !locationId) {
      set({ isAssigning: false, assignError: 'Location data is incomplete. Cannot assign.' });
      return;
    }

    const settingsJson = buildSettingsJson(selectedLocation, kioskDetails?.settings ?? null);

    const { registerKioskUseCase } = servicesContainer.getServices();
    const result = await registerKioskUseCase.execute(
      machineId,
      countryCode,
      partyId,
      locationId,
      settingsJson
    );

    if (result.hasError) {
      set({ isAssigning: false, assignError: result.errorMessage });
      setTimeout(() => {
        const current = useAssignKioskStore.getState();
        if (current.assignError === result.errorMessage) {
          useAssignKioskStore.setState({ assignError: null });
        }
      }, 10_000);
      return;
    }

    // Update kioskDetails to reflect the new assignment and new settings,
    // then re-fetch locations so connector data is merged from the saved settings.
    // If kioskDetails was null (machine never registered before), create it from
    // the location data so isAssigned becomes true and the blue header shows.
    set({
      isAssigning: false,
      assignSuccess: true,
      assignSuccessMessage: isConnectorRemoval ? 'Connectors removed.' : 'Kiosk assigned successfully.',
      kioskDetails: {
        ...(kioskDetails ?? {
          machineId: parseInt(machineId),
          operator: null,
          kioskSettings: null,
          operatorSettings: null,
        }),
        locationId,
        locationName: selectedLocation.name,
        countryCode,
        partyId,
        settings: settingsJson,
      },
    });
    setTimeout(() => {
      useAssignKioskStore.setState({ assignSuccess: false });
    }, 4_000);
    await get().fetchLocations();
  },

  // ─── dismissAssignError ───────────────────────────────────────────────────
  dismissAssignError: () => set({ assignError: null }),

  // ─── reset ────────────────────────────────────────────────────────────────
  reset: () => set(initialState),
}));
